import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function UsedServices ({
  data,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const formattedDate = selectedDate.toFormat ('MMM yyyy')
  const token = AuthService.getToken ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [usedServices, setUsedServices] = useState (null)

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()

      const filters = `start_date=${startDate}&end_date=${endDate}`
        
      const { data: usedServices } = await cutileiApi.get (
        `/admin/dashboard/used_services?${filters}`, requestConfig
      )

      setUsedServices ([
        ['Serviços', 'Mão', 'Pé', 'Pé e mão', 'Esmaltação - Mão'],
        ...Object.keys (usedServices).map (month => ([
          month,
          usedServices[month]['Mão'].totalSold,
          usedServices[month]['Pé'].totalSold,
          usedServices[month]['Pé e mão'].totalSold,
          usedServices[month]['Esmaltação - Mão'].totalSold
        ]))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!data || Object.keys (data).length === 0) {
    return null
  }

  const monthUsedServices = [
    ['Serviços', 'Quantidade'],
    ['Mão', data[formattedDate]?.['Mão'].totalSold ?? 0],
    ['Pé', data[formattedDate]?.['Pé'].totalSold ?? 0],
    ['Pé e mão', data[formattedDate]?.['Pé e mão'].totalSold ?? 0],
    ['Esmaltação - Mão', data[formattedDate]?.['Esmaltação - Mão'].totalSold ?? 0]
  ]

  return (
    <InfoContainer>
      <SubTitle>Serviços utilizados - {selectedDate.toFormat ('MMMM / yyyy')}</SubTitle>
      <PieChart
        data={monthUsedServices}
        format='amount'
        options={{colors: ['#FF3939', '#44CF6C', '#35A7FF', '#FFC100']}}
      />
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Serviços utilizados'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            name='periodFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart
            data={usedServices}
            options={{
              isStacked: false,
              colors: ['#FF3939', '#44CF6C', '#35A7FF', '#FFC100'],
              chartArea: {
                top: 35, left: 110, width: '100%', height: '80%'
              },
              vAxis: {
                title: 'Serviços utilizados',
                titleTextStyle: {
                  bold: true
                }
              }
            }}
          />
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
